import React, { useEffect, useMemo, useState } from "react";
import { Typography } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { SbTable, SbTag, SbTooltip, SbNotification } from "uiCore";
import { BackLinkStyled } from "../../RoleDetailV2.styles";
import {
  InfoCardStyled,
  InfoStatStyled,
} from "../../RedeploymentReductionDetailV2/RedeploymentReductionDetailV2.styles";
import { DetailHeadingRowStyled, PlanTableWrapperStyled } from "../ViewPlanningV2.styles";
import {
  getTalentMobilityJdActiveEmployees,
  getViewPlanningRedeploymentRoleEmployees,
} from "Api/apiService";
import { usePlanRoleDetailColumns } from "../useViewPlanningColumns";
import {
  tableEmptyLocale,
  mapJdActiveEmployeesToOccupants,
  getAvailableEmployeeIds,
  buildTargetedRoleIdsByEmployee,
  buildShortlistReasonByEmployee,
  resolveCutoff,
} from "../ViewPlanningV2.utils";
import PlanNextRolesPanel from "./PlanNextRolesPanel";
import { IndexInfoLabel } from "../../components/IndexTooltip";

const { Title, Text } = Typography;

// The selected role's Reduction Detail mirror — the role's occupants from
// GET /talent-mobility/jd-active-employees ranked by SMI, filtered down to
// the employees the saved plan marked available
// (GET /view-planning/redeployment-plan/role-employees). Rank and status
// keep each person's position in the full ranking, and the header stats
// still describe the whole reduction (current, target, available at-risk).
const PlanRoleDetail = ({ selectedPlan, selectedRole, onBack }) => {
  const [occupants, setOccupants] = useState(null); // null = loading
  const [planEmployees, setPlanEmployees] = useState(null); // null = loading
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const response = await getTalentMobilityJdActiveEmployees(selectedRole.jd_sb_unique_id);
        if (cancelled) return;
        const employees = Array.isArray(response?.employees) ? response.employees : [];
        setOccupants(mapJdActiveEmployeesToOccupants(employees));
      } catch (error) {
        if (cancelled) return;
        setOccupants([]);
        SbNotification.error(
          "Unable to load role occupants",
          error?.response?.data?.message || "Please try again."
        );
      }
    })();

    (async () => {
      try {
        const response = await getViewPlanningRedeploymentRoleEmployees(
          selectedRole.redeploy_plan_unique_id
        );
        if (cancelled) return;
        setPlanEmployees(Array.isArray(response?.employees) ? response.employees : []);
      } catch (error) {
        if (cancelled) return;
        setPlanEmployees([]);
        SbNotification.error(
          "Unable to load plan availability",
          error?.response?.data?.message || "Please try again."
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedRole]);

  // The saved plan's availability — who was marked available, and which
  // roles each of them was targeted to.
  const availableEmployeeIds = useMemo(() => getAvailableEmployeeIds(planEmployees), [planEmployees]);
  const targetedRoleIdsByEmployee = useMemo(
    () => buildTargetedRoleIdsByEmployee(planEmployees),
    [planEmployees]
  );
  const shortlistReasonByEmployee = useMemo(
    () => buildShortlistReasonByEmployee(planEmployees),
    [planEmployees]
  );

  // Both fetches feed one table (occupants ranked, then filtered by the
  // plan's availability), so the page is "loading" until both are in —
  // otherwise the fast response renders misleading zeros/empty states while
  // the slow one is still in flight.
  const isLoading = occupants === null || planEmployees === null;

  const allOccupants = occupants || [];
  const cutoff = resolveCutoff(selectedRole.target_position, allOccupants.length);
  // Only the plan's marked-available employees are listed; rank and status
  // keep each person's position in the full SMI ranking. Memoized so row
  // expand/collapse re-renders don't defeat the columns hook's useMemo.
  const occupantRows = useMemo(
    () =>
      (occupants || [])
        .map((row, index) => ({ ...row, rank: index }))
        .filter((row) => availableEmployeeIds.has(row.person.id)),
    [occupants, availableEmployeeIds]
  );

  const columns = usePlanRoleDetailColumns(cutoff, occupantRows, shortlistReasonByEmployee);

  const toggleRow = (personId) =>
    setExpandedRowKeys((prev) => (prev.includes(personId) ? prev.filter((k) => k !== personId) : [...prev, personId]));

  // Fragment root so the tab pane's flex column (TabsBodyStyled) can
  // hand the remaining height to the table wrapper, which owns the scroll.
  return (
    <>
      <BackLinkStyled onClick={onBack}>
        <ArrowLeftOutlined /> Back to Roles
      </BackLinkStyled>
      <DetailHeadingRowStyled>
        <Title level={5}>
          {selectedPlan.plan_name} — {selectedRole.title || selectedRole.jd_sb_unique_id}
        </Title>
      </DetailHeadingRowStyled>
      <Text type="secondary">
        Employees marked available in this plan, ranked by <IndexInfoLabel index="SMI" /> against the full role. Everyone below the target headcount is at risk in this reduction.
      </Text>

      <InfoCardStyled>
        {/* Centered inline (not in the shared InfoStatStyled definition) —
            that component is also Reduction Detail's own header, and its own
            "At Risk" stat below isn't part of this centering ask. */}
        <InfoStatStyled style={{ alignItems: "center" }}>
          <span className="label">Current Positions</span>
          <span className="value">{isLoading ? "—" : allOccupants.length}</span>
        </InfoStatStyled>
        <InfoStatStyled style={{ alignItems: "center" }}>
          <span className="label">Target Positions</span>
          <span className="value">{isLoading ? "—" : cutoff}</span>
        </InfoStatStyled>
        <InfoStatStyled>
          <span className="label">At Risk</span>
          <span className="value">
            {isLoading ? (
              "—"
            ) : (
              <SbTooltip content="At-risk people marked available for redeployment in this plan.">
                <SbTag color={occupantRows.length > 0 ? "error" : "success"} style={{ fontSize: 14 }}>{occupantRows.length}</SbTag>
              </SbTooltip>
            )}
          </span>
        </InfoStatStyled>
      </InfoCardStyled>

      <PlanTableWrapperStyled>
        <SbTable
          columns={columns}
          dataSource={occupantRows}
          rowKey={(record) => record.person.id}
          sticky
          onRow={(record) => ({ onClick: () => toggleRow(record.person.id) })}
          expandable={{
            expandedRowKeys,
            showExpandColumn: false,
            expandedRowRender: (record) => (
              <PlanNextRolesPanel
                person={record.person}
                targetedRoleIds={targetedRoleIdsByEmployee.get(record.person.id) || new Set()}
              />
            ),
          }}
          locale={tableEmptyLocale(isLoading ? null : occupantRows, "No employees marked available for this role in this plan.")}
        />
      </PlanTableWrapperStyled>
    </>
  );
};

export default PlanRoleDetail;
